import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import StickyMobileCTA from "@/components/landing/StickyMobileCTA";
import { POSTS } from "@/seo/content";
import { WHATSAPP_URL } from "@/i18n/translations";

const BlogPost = () => {
  const { slug } = useParams();
  const data = POSTS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) return <Navigate to="/blog" replace />;

  const url = `https://maif.ma/blog/${data.slug}`;
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: data.title,
    description: data.description,
    author: { "@type": "Person", name: "Achraf AYACH" },
    publisher: { "@type": "Organization", name: "Moroccan AI Factory" },
    datePublished: "2026-04-01",
    mainEntityOfPage: url,
    image: "https://maif.ma/favicon.ico",
  };
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-maif text-maif min-h-screen">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.description} />
        <meta name="keywords" content={data.keywords.join(", ")} />
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="en" href={url} />
        <link rel="alternate" hrefLang="fr" href={url} />
        <link rel="alternate" hrefLang="ar" href={url} />
        <link rel="alternate" hrefLang="x-default" href={url} />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Moroccan AI Factory" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.title} />
        <meta name="twitter:description" content={data.description} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <Header />
      <main className="pt-28 sm:pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-maif-muted hover:text-maif transition mb-8"
          >
            <ArrowLeft size={14} /> All articles
          </Link>

          <div className="eyebrow">Blog · {data.date}</div>
          <h1 className="mt-4 font-display font-light text-4xl md:text-5xl tracking-tighter text-maif leading-[1.1]">
            {data.h1}
          </h1>
          <p className="mt-6 text-lg md:text-xl leading-relaxed text-maif-muted">{data.intro}</p>

          <div className="mt-12 space-y-10">
            {data.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight text-maif">
                  {s.h2}
                </h2>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-maif-muted">
                  {s.body}
                </p>
              </section>
            ))}
          </div>

          <div className="mt-16 rounded-2xl bg-maif-2 p-8 md:p-10 border border-sand-soft">
            <h2 className="font-display text-2xl text-maif tracking-tight">Talk to us</h2>
            <p className="mt-3 text-maif-muted">
              30-min discovery call. We'll show you exactly how this applies to your business — no pitch.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/#lead" className="btn-primary justify-center">Book free call <ArrowUpRight size={16} /></Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary justify-center">WhatsApp</a>
            </div>
          </div>

          <section className="mt-20">
            <h2 className="font-display text-2xl md:text-3xl tracking-tight text-maif">FAQ</h2>
            <div className="mt-6 divide-y divide-sand-soft border-y border-sand-soft">
              {data.faqs.map((f, i) => (
                <details key={i} className="py-6 group">
                  <summary className="cursor-pointer font-display text-lg text-maif list-none flex items-center justify-between gap-4">
                    {f.q}
                    <span className="text-terracotta group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-4 text-maif-muted leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {data.related && (
            <section className="mt-20">
              <div className="eyebrow">Keep reading</div>
              <h2 className="mt-3 font-display text-2xl tracking-tight text-maif">Related articles</h2>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.related.map((r) => {
                  const post = POSTS[r];
                  if (!post) return null;
                  return (
                    <Link
                      key={r}
                      to={`/blog/${r}`}
                      className="block rounded-xl bg-maif-2 border border-sand-soft p-5 hover:border-sand-mid transition"
                    >
                      <div className="eyebrow text-[10px]">{post.date}</div>
                      <div className="mt-2 font-display text-maif">{post.h1}</div>
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-terracotta">
                        Read <ArrowUpRight size={12} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default BlogPost;
